import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ImageNotSupported as ImageNotSupportedIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ENTITY_TYPE_BY_REPORT_TYPE } from '@/admins/SmartReports/constants';
import { useCreateReport } from '@/admins/SmartReports/hooks/useCreateReport';
import { useGetEntities } from '@/admins/SmartReports/hooks/useGetEntities';
import { useGetReportTemplates } from '@/admins/SmartReports/hooks/useGetReportTemplates';
import { useGetReportTypes } from '@/admins/SmartReports/hooks/useGetReportTypes';
import { AdminsPageShell } from '@/components/AdminsPageShell';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { useDebounce } from '@/hooks/useDebounce';
import { ADMINS_SMART_REPORTS_PATH } from '@/utils/constants';
import { useHistory } from 'react-router-dom';
import { styles } from './styles';

const ReportTemplateList = ({ templates, template, setTemplate }) => {
  return (
    <Box display="flex" width="100%" alignItems="center" sx={styles.row}>
      <Box flex={1}>
        <Typography variant="h5" sx={styles.label}>
          Template
        </Typography>
      </Box>
      <Box flex={5}>
        <ImageList cols={4} sx={styles.templateList}>
          {/* No template selected item */}
          <ImageListItem key="no-template" onClick={() => setTemplate(null)} sx={styles.template}>
            <Box sx={styles.noTemplate} className={!template ? 'selected' : ''}>
              <ImageNotSupportedIcon fontSize="large" />
            </Box>
            <ImageListItemBar title="Blank" position="below" />
          </ImageListItem>

          {/* Templates */}
          {templates.map((reportTemplate) => (
            <ImageListItem
              key={reportTemplate.id}
              onClick={() => setTemplate(reportTemplate)}
              sx={styles.template}
              className={reportTemplate?.id === template?.id ? 'selected' : ''}
            >
              <img
                src={reportTemplate.thumbnailUrl}
                srcSet={reportTemplate.thumbnailUrl}
                alt={reportTemplate.title}
                loading="lazy"
              />
              <ImageListItemBar
                title={reportTemplate.name}
                subtitle={reportTemplate.description}
                position="below"
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Box>
    </Box>
  );
};

const ReportTypeSelect = ({ reportType, setReportType }) => {
  const { reportTypes } = useGetReportTypes();

  return (
    <Box display="flex" width="100%" alignItems="center" sx={styles.row}>
      <Box flex={1}>
        <Typography variant="h5" sx={styles.label}>
          Report Type
        </Typography>
      </Box>
      <Box flex={5}>
        <FormControl variant="outlined" sx={styles.formControl}>
          <InputLabel id="report-type-label">Choose a report type</InputLabel>
          <Select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            variant="outlined"
            sx={styles.select}
            IconComponent={ExpandMoreIcon}
          >
            {reportTypes?.map((reportTypeOption) => (
              <MenuItem key={reportTypeOption.id} value={reportTypeOption.id} sx={styles.menuItem}>
                {reportTypeOption.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

const EntitySelect = ({ entity, setEntity, reportType }) => {
  const [entitySearch, setEntitySearch] = useState('');
  const doubouncedEntitySearch = useDebounce(entitySearch, 300);
  const { entities, loading } = useGetEntities({
    search: doubouncedEntitySearch,
    reportTypeId: reportType,
  });

  useEffect(() => {
    if (reportType) {
      // Reset the entity when the report type changes
      setEntitySearch('');
      setEntity(null);
    }
  }, [reportType]);

  return (
    <Box display="flex" width="100%" alignItems="center" sx={styles.row}>
      <Box flex={1}>
        <Typography variant="h5" sx={styles.label}>
          Entity
        </Typography>
      </Box>
      <Box flex={5}>
        <Autocomplete
          sx={styles.autocomplete}
          autoComplete
          variant="outlined"
          value={entity}
          options={entities}
          loading={loading}
          getOptionLabel={(option) => option?.label || ''}
          onChange={(_, newValue) => setEntity(newValue || '')}
          noOptionsText="No entities found"
          inputValue={entitySearch}
          onInputChange={(_, newInputValue) => setEntitySearch(newInputValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          popupIcon={<ExpandMoreIcon />}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={styles.autocompleteInput}
              label="Choose an entity"
              variant="outlined"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                },
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export const NewSmartReport = () => {
  const history = useHistory();
  const { setSnack } = useSnackBarContext();

  const [entity, setEntity] = useState('');
  const [template, setTemplate] = useState('');
  const [reportType, setReportType] = useState('');

  const { reportTypes } = useGetReportTypes();
  const { templates } = useGetReportTemplates();

  useEffect(() => {
    if (!reportType && reportTypes.length > 0) {
      // Set the default report type to the first one in the list
      setReportType(reportTypes[0].id);
    }
  }, [reportTypes]);

  const {
    createReport,
    data: createReportResponse,
    loading: loadingCreate,
    error,
  } = useCreateReport();

  useEffect(() => {
    if (error) {
      setSnack({
        open: true,
        message: 'An error occurred when creating the smart report.',
        severity: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    if (createReportResponse) {
      setSnack({ open: true, message: 'Smart Report created successfully!', severity: 'success' });
      // redirect to the page to edit the report
      const reportId = createReportResponse.createSmartReport?.id;
      if (reportId) {
        history.push(`${ADMINS_SMART_REPORTS_PATH}/${reportId}/edit`);
      }
    }
  }, [createReportResponse]);

  const onCreate = async () => {
    const selectedReportType = reportTypes.find(
      (reportTypeOption) => reportTypeOption.id === reportType,
    );
    const entityType = ENTITY_TYPE_BY_REPORT_TYPE[selectedReportType?.name];
    createReport({
      reportTypeId: reportType,
      entityId: entity.id,
      entityType: entityType,
      reportTemplate: template,
    });
  };

  const isCreateEnabled = reportType && entity && !loadingCreate;

  return (
    <AdminsPageShell title="Add New Report">
      <Button
        variant="text"
        color="secondary"
        href={ADMINS_SMART_REPORTS_PATH}
        startIcon={<ArrowBackIcon />}
      >
        <Typography variant="body1" sx={{ color: 'secondary' }}>
          Back to reports
        </Typography>
      </Button>
      <Box sx={styles.container}>
        <ReportTypeSelect reportType={reportType} setReportType={setReportType} />
        <EntitySelect entity={entity} setEntity={setEntity} reportType={reportType} />
        <ReportTemplateList templates={templates} template={template} setTemplate={setTemplate} />

        {/* Buttons */}
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Box flex={1}></Box>
          <Box flex={5}>
            <Button
              variant="contained"
              color="primary"
              sx={styles.cancelButton}
              href={ADMINS_SMART_REPORTS_PATH}
              disabled={loadingCreate}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              sx={styles.button}
              onClick={onCreate}
              disabled={!isCreateEnabled}
            >
              Create
            </Button>
          </Box>
        </Box>
      </Box>
    </AdminsPageShell>
  );
};
